import type { ProviderFile } from "fileParsing/types";
import { startsWithUint8Arrays } from "@utils/array";
import Uint8ToObj from "@utils/uint8ToObj";
import Papa from "papaparse";
import { parseHTML as parseDOM } from "./parseHTML";
import { parse, simplify } from "./parseXml";
import type { StructureType } from "./types";

export function parseFn(file: ProviderFile): {
  isNotEmpty: boolean;
  structure: StructureType;
} {
  return {
    isNotEmpty: (file.parserData.byteData?.length ?? 0) > 2,
    structure: {
      labelFileName: file.fileName.substring(0, file.fileName.lastIndexOf(".")),
      labelFilePath: file.path,
      labelParentFolderName: getParentFolderName(file.path, file.fileName),
      labelDate: file.date.toUTCString(),
      data: parseRawFile(file.parserData?.byteData, file.fileName, file.mimeType),
    },
  };
}

function getParentFolderName(path: string, fileName: string): string {
  const path2 = path.endsWith(fileName) ? path.substring(0, path.length - fileName.length) : path;
  const clipped = path2.endsWith("/") ? path2.substring(0, path2.length - 1) : path2;
  const ix = clipped.lastIndexOf("/");
  return clipped.substring(ix + 1);
}

function parseRawFile(byteData: Uint8Array | undefined | null, fileName: string, mimeType: string): unknown[] {
  // ignore empty files
  if (!byteData || byteData.length === 0) return [];

  // ignore hidden files
  if (fileName.startsWith(".")) return [];

  const xmlPrefix: Uint8Array = new TextEncoder().encode("<?xml");
  try {
    if (fileName.endsWith(".json") || mimeType === "application/json") {
      return parseJSON(byteData);
    } else if (fileName.endsWith(".js")) {
      return parseJavascript(byteData);
    } else if (fileName.endsWith(".csv")) {
      return parseCSV(byteData);
    } else if (fileName.endsWith(".html") || fileName.endsWith(".htm")) {
      return parseHTML(byteData);
    } else if (
      fileName.endsWith(".tcx") ||
      fileName.endsWith(".xml") ||
      mimeType === "application/xml" ||
      mimeType === "text/xml" ||
      mimeType === "application/xml-external-parsed-entity" ||
      mimeType === "text/xml-external-parsed-entity" ||
      mimeType === "application/xml-dtd" ||
      startsWithUint8Arrays(byteData, xmlPrefix)
    ) {
      return parseXML(byteData);
    }
  } catch (e) {
    console.warn(`Failed parsing ${fileName}:`);
    console.warn(e);
  }
  return [];
}

function parseJSON(data: Uint8Array): unknown[] {
  let json: unknown;

  // the maximum length of bytes
  const MAX_STRING_LENGTH = 2 ** 29 - 24;

  if (data.length < MAX_STRING_LENGTH) {
    try {
      const string = new TextDecoder("utf-8").decode(data);
      json = JSON.parse(string);
    } catch {
      try {
        return parseLineJsonString(data);
      } catch (e) {
        console.log("Could not parse JSON file: ", e);
      }
    }
  } else {
    try {
      json = Uint8ToObj.parse(data, { decodeStringsAsUtf8: true });
    } catch {
      try {
        json = Uint8ToObj.parse(data);
      } catch {
        try {
          return parseLineJson(data);
        } catch (e) {
          console.log("Could not parse JSON file: ", e);
        }
      }
    }
  }

  return [json];
}

function guessLineEnding(data: Uint8Array): number {
  const r = 0x0a; // line feed
  const l = 0x0d; // carriage return
  return data.indexOf(r) > data.indexOf(l) ? r : l;
}

/**
 * "Line JSON" are normal json files where each line has a
 * single json object without an comma at the end of the line.
 *
 * For example:
 *
 * {hello: "world"}
 * {hello: "wrangler", foo: "bar"}
 * {hello: "its me"}
 *
 * @param inputData raw data
 * @returns parsed data
 */
function parseLineJsonString(inputData: Uint8Array): unknown[] {
  const lineEnding = String.fromCharCode(guessLineEnding(inputData));
  const decodedString = new TextDecoder("utf-8").decode(inputData);
  const joinedJsonString =
    "[" +
    decodedString
      .split(lineEnding)
      .filter((l) => l.length > 0)
      .join(",") +
    "]";
  const res = JSON.parse(joinedJsonString);
  return res;
}

/**
 * "Line JSON" are normal json files where each line has a
 * single json object without an comma at the end of the line.
 * This method is much slower, but works on files that are
 * larger then the max string length.
 *
 * For example:
 *
 * {hello: "world"}
 * {hello: "wrangler", foo: "bar"}
 * {hello: "its me"}
 *
 * @param inputData raw data
 * @returns parsed data
 */
function parseLineJson(inputData: Uint8Array): unknown[] {
  const newline = guessLineEnding(inputData);
  let lastPos = 0;
  let nextPos = inputData.indexOf(newline);
  const res: unknown[] = [];
  while (nextPos !== -1) {
    const point: unknown = Uint8ToObj.parse(inputData.slice(lastPos, nextPos));
    res.push(point);
    lastPos = nextPos + 1;
    nextPos = inputData.indexOf(newline, nextPos + 1);
  }
  return res;
}

/**
 * Javascript files are from Twitter provider.
 * They are .js files where they export json as a module.
 * The primary use case of this is to support their html viewer.
 * The parser needs to ignore all characters until the equal sign.
 * They look like:
 *
 * window.YTD.something.part0 = [{}, {}, ...]
 *
 * Where {} contains the data.
 *
 * @param inputData raw data
 * @returns parsed data structure
 */
function parseJavascript(inputData: Uint8Array): unknown[] {
  const windowPrefix: Uint8Array = new TextEncoder().encode("window.");
  if (startsWithUint8Arrays(inputData, windowPrefix)) {
    const parsedObject = Uint8ToObj.parse(inputData, {
      skipUntilEqualSign: true,
    });
    return [parsedObject];
  }
  return [];
}

/**
 * the csv file format is a structured text file where comma is used to separate values.
 * for example:
 *
 * name, age, dob
 * alice, 7, 1865-10-04
 * bob, 44, 1970-01-01
 *
 * @param data raw data
 * @returns parsed data
 */
function parseCSV(data: Uint8Array): unknown[] {
  const string = new TextDecoder("utf-8").decode(data);
  // replace all line endings with carriage return
  // to support different line endings in one file
  const newline = "\n";
  const csv = Papa.parse(string.replace(/[\r\n]+/g, newline), {
    header: true,
    skipEmptyLines: true,
  });
  return [csv.data];
}

/**
 * the html file format is a markup language for browsers.
 * to display data they must rely on heavly repeating sequences.
 * we try to detect those sequences and parse them.
 * @param data raw data
 * @returns parsed data
 */
function parseHTML(data: Uint8Array): unknown[] {
  const string = new TextDecoder("utf-8")
    .decode(data)
    // remove whitespace
    .replaceAll(/\s\s+/g, " ");
  const dom = parseDOM(string);
  return [dom];
}

/**
 * simple and bad xml parsing.
 * @param data raw data
 * @returns parsed data
 */
function parseXML(data: Uint8Array): unknown[] {
  const string = new TextDecoder("utf-8")
    .decode(data)
    // remove whitespace
    .replaceAll(/\s\s+/g, " ");
  const xml1 = simplify(parse(string));
  return [xml1];
}
